import type { CharacterStyle } from '../core/characters';
import { FIGURE, type FigureBuild } from './figure';
import { PRIM_CAPSULE, PRIM_DISK, PRIM_ROUNDED_BOX, type Primitive } from './sdf/primitives';

export type StyleParts = Pick<FigureBuild, 'head' | 'handMain' | 'handOff'> & {
  hip: { x: number; y: number }; shoulder: { x: number; y: number }; headAngle?: number; look?: { x: number; y: number };
  handAngles?: FigureBuild['handAngles']; motion?: FigureBuild['motion'];
};

type SkinPanel = { panel: Primitive[]; trace: Primitive[] };

/** Inset details preserve the player's colored outline, face, and physical silhouette. */
export function characterSkin(parts: StyleParts, style: CharacterStyle): { torso: SkinPanel; main: SkinPanel; off: SkinPanel } | null {
  if (!style.skin || style.skin === 'classic') return null;
  const torsoScale = FIGURE.torsoR / 0.17, handScale = FIGURE.handR / 0.12;
  const cap = (ax: number, ay: number, bx: number, by: number, r: number): Primitive => ({ kind: PRIM_CAPSULE, ax, ay, bx, by, r });
  const disk = (x: number, y: number, r: number): Primitive => ({ kind: PRIM_DISK, ax: x, ay: y, bx: x, by: y, r });
  const { hip, shoulder } = parts;
  const dx = shoulder.x - hip.x, dy = shoulder.y - hip.y, length = Math.hypot(dx, dy) || 1;
  const point = (t: number, offset = 0) => ({ x: hip.x + dx * t + offset * torsoScale * dy / length, y: hip.y + dy * t - offset * torsoScale * dx / length });
  const line = (a: ReturnType<typeof point>, b: ReturnType<typeof point>, r: number) => cap(a.x, a.y, b.x, b.y, r * torsoScale);
  const torso: SkinPanel = { panel: [line(point(0.04), point(0.91), 0.13)], trace: [] };
  if (style.skin === 'circuit') {
    // Parallel rails take two sharp turns around the markings in the middle of the chest.
    for (const side of [-1, 1]) {
      const path = [point(0.07, side * 0.09), point(0.62, side * 0.09), point(0.83, side * 0.045), point(1.01, side * 0.045)];
      for (let i = 1; i < path.length; i++) torso.trace.push(line(path[i - 1]!, path[i]!, 0.024));
      torso.trace.push(disk(path[0]!.x, path[0]!.y, 0.035 * torsoScale));
    }
  } else {
    const center = point(0.72);
    // Eight short segments make an open light ring; no animated flicker or face-covering visor.
    for (let i = 0; i < 8; i++) {
      const angle = i * Math.PI / 4, end = angle + Math.PI / 4 * 0.7;
      torso.trace.push(cap(center.x + Math.cos(angle) * 0.095 * torsoScale, center.y + Math.sin(angle) * 0.095 * torsoScale,
        center.x + Math.cos(end) * 0.095 * torsoScale, center.y + Math.sin(end) * 0.095 * torsoScale, 0.022 * torsoScale));
    }
    torso.trace.push(line(point(0.04), point(0.3), 0.025));
  }
  const wrist = (hand: StyleParts['handMain'], angle: number): SkinPanel => {
    const c = Math.cos(angle), s = Math.sin(angle), x = hand.x - c * 0.065 * handScale, y = hand.y - s * 0.065 * handScale;
    if (style.skin === 'pulse') return { panel: [disk(x, y, 0.091 * handScale)], trace: [disk(x, y, 0.047 * handScale)] };
    return { panel: [cap(x + s * 0.075 * handScale, y - c * 0.075 * handScale, x - s * 0.075 * handScale, y + c * 0.075 * handScale, 0.065 * handScale)],
      trace: [cap(x + s * 0.07 * handScale, y - c * 0.07 * handScale, x - s * 0.07 * handScale, y + c * 0.07 * handScale, 0.023 * handScale)] };
  };
  return { torso, main: wrist(parts.handMain, parts.handAngles?.main ?? 0), off: wrist(parts.handOff, parts.handAngles?.off ?? 0) };
}

/** Wearable details follow the same joints as the body, including a tumbling ragdoll head. */
export function characterDetails(parts: StyleParts, style: CharacterStyle) {
  const light: Primitive[] = [], dark: Primitive[] = [];
  const back: Primitive[] = [], mainWear: Primitive[] = [], offWear: Primitive[] = [];
  const headScale = parts.head.r / 0.36, torsoScale = FIGURE.torsoR / 0.17, handScale = FIGURE.handR / 0.12;
  const cap = (ax: number, ay: number, bx: number, by: number, r = 0.035): Primitive => ({ kind: PRIM_CAPSULE, ax, ay, bx, by, r });
  const c = Math.cos(parts.headAngle ?? 0), s = Math.sin(parts.headAngle ?? 0);
  const headLine = (ax: number, ay: number, bx: number, by: number, r: number, ox = 0, oy = 0) => {
    const point = (x: number, y: number) => ({ x: parts.head.x + (x * headScale + ox) * c - (y * headScale + oy) * s,
      y: parts.head.y + (x * headScale + ox) * s + (y * headScale + oy) * c });
    const a = point(ax, ay), b = point(bx, by);
    return cap(a.x, a.y, b.x, b.y, r * headScale);
  };
  if (style.accessory === 'headband') {
    // Keep the forehead band above the brows; personality should still read when equipped.
    const bandY = 0.24 + Math.max(0, (parts.look?.y ?? 0) / headScale);
    const bandX = Math.min(0.255, Math.sqrt(Math.max(0.01, 0.355 ** 2 - bandY ** 2)));
    light.push(headLine(-bandX, bandY, bandX, bandY, 0.036));
    const side = (parts.look?.x ?? 1) < 0 ? -1 : 1;
    light.push(headLine(-bandX * side, bandY, -(bandX + 0.195) * side, bandY - 0.135, 0.035), headLine(-bandX * side, bandY, -(bandX + 0.175) * side, bandY, 0.028));
  } else if (style.accessory === 'glasses') {
    const ox = parts.look?.x ?? 0, oy = parts.look?.y ?? 0;
    const glassLine = (ax: number, ay: number, bx: number, by: number, r: number) => headLine(ax, ay, bx, by, r, ox, oy);
    for (const x of [-0.09, 0.09]) {
      // Thin, symmetric frames leave separate pupils rather than merging into a dark visor.
      dark.push(glassLine(x - 0.078, 0.104, x + 0.078, 0.104, 0.012), glassLine(x - 0.078, -0.049, x + 0.078, -0.049, 0.013));
      dark.push(glassLine(x - 0.078, 0.104, x - 0.078, -0.049, 0.013), glassLine(x + 0.078, 0.104, x + 0.078, -0.049, 0.013));
    }
    dark.push(glassLine(-0.012, 0.047, 0.012, 0.047, 0.012));
  } else if (style.accessory === 'wraps' || style.accessory === 'gloves') {
    for (const [hand, angle, wear] of [[parts.handMain, parts.handAngles?.main ?? 0, mainWear], [parts.handOff, parts.handAngles?.off ?? 0, offWear]] as const) {
      const c = Math.cos(angle), s = Math.sin(angle);
      const wrist = { x: hand.x - c * 0.07 * handScale, y: hand.y - s * 0.07 * handScale };
      wear.push(cap(wrist.x + s * 0.08 * handScale, wrist.y - c * 0.08 * handScale, wrist.x - s * 0.08 * handScale, wrist.y + c * 0.08 * handScale, 0.045 * handScale));
      if (style.accessory === 'gloves') wear.push({ kind: PRIM_ROUNDED_BOX, ax: hand.x + c * 0.025 * handScale, ay: hand.y + s * 0.025 * handScale, bx: 0.14 * handScale, by: 0.125 * handScale, r: 0.075 * handScale, rot: angle });
      light.push(...wear);
    }
  } else if (style.accessory === 'scarf') {
    const side = (parts.motion?.vx ? Math.sign(parts.motion.vx) : Math.sign(parts.look?.x ?? 1)) || 1;
    const flutter = parts.motion?.reduced ? 0 : Math.sin((parts.motion?.phase ?? 0) * 7) * 0.035;
    const lift = Math.min(0.23, Math.abs(parts.motion?.vx ?? 0) * 0.035);
    const { shoulder } = parts;
    back.push(cap(shoulder.x - side * 0.12 * torsoScale, shoulder.y + 0.055, shoulder.x - side * 0.4, shoulder.y - 0.1 + lift, 0.07 * torsoScale),
      cap(shoulder.x - side * 0.4, shoulder.y - 0.1 + lift, shoulder.x - side * 0.55, shoulder.y - 0.25 + lift + flutter, 0.075 * torsoScale));
    light.push(cap(shoulder.x - 0.18 * torsoScale, shoulder.y + 0.035, shoulder.x + 0.18 * torsoScale, shoulder.y + 0.035, 0.072 * torsoScale));
  }
  const wear = light.slice();
  const { hip, shoulder } = parts;
  const dx = shoulder.x - hip.x, dy = shoulder.y - hip.y, length = Math.hypot(dx, dy) || 1;
  const bodyPoint = (t: number, offset: number) => ({ x: hip.x + dx * t + offset * torsoScale * dy / length, y: hip.y + dy * t - offset * torsoScale * dx / length });
  const a = bodyPoint(0.48, -0.11), b = bodyPoint(0.48, 0.11), middle = bodyPoint(0.28, 0);
  if (style.marking === 'stripe') light.push(cap(a.x, a.y, b.x, b.y, 0.043 * torsoScale));
  if (style.marking === 'chevron') light.push(cap(a.x, a.y, middle.x, middle.y, 0.033 * torsoScale), cap(middle.x, middle.y, b.x, b.y, 0.033 * torsoScale));
  if (style.marking === 'spots') for (const t of [0.3, 0.7]) { const p = bodyPoint(t, 0); light.push({ kind: PRIM_DISK, ax: p.x, ay: p.y, bx: p.x, by: p.y, r: 0.049 * torsoScale }); }
  return { light, dark, markings: light.slice(wear.length), wear, back, mainWear, offWear };
}
